import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { UserPlus, Eye, EyeOff } from 'lucide-react';

const Register = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    preferred_language: i18n.language || 'sk'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resending, setResending] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Hesla sa nezhoduju');
      return;
    }

    if (formData.password.length < 6) {
      setError('Heslo musi mat minimalne 6 znakov');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      const response = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.access_token) {
        localStorage.setItem('sunify_token', data.access_token);
        localStorage.setItem('sunify_user', JSON.stringify(data.user));
        window.location.href = '/';
      } else {
        setError(data.detail || 'Registracia zlyhala');
      }
    } catch (err) {
      setError('Chyba pripojenia k serveru');
    }
    
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await fetch(`${API}/api/auth/resend-verification?email=${encodeURIComponent(formData.email)}`, {
        method: 'POST'
      });
    } catch (err) {}
    setResending(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (registered) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-4" data-testid="verify-title">Skontrolujte email</h2>
            <p className="text-gray-600 mb-2">
              Na adresu <strong>{formData.email}</strong> sme odoslali overovaci odkaz.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Kliknite na odkaz v emaili pre aktivaciu uctu.
            </p>
            <button
              onClick={handleResend}
              disabled={resending}
              className="text-orange-500 hover:text-orange-600 font-semibold text-sm"
              data-testid="resend-btn"
            >
              {resending ? t('auth.resending') : t('auth.resend_verification')}
            </button>
            <div className="mt-6">
              <Link to="/login" className="text-gray-500 hover:text-gray-700 text-sm">
                Prejst na prihlasenie
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mb-4">
              <UserPlus className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold" data-testid="register-title">{t('auth.register_title')}</h2>
            <p className="text-gray-600 mt-2">{t('auth.register_subtitle')}</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="name">
                {t('auth.name')}
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder={t('auth.name_placeholder')}
                data-testid="register-name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="email">
                {t('auth.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="vas@email.com"
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="phone">
                {t('auth.phone')}
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="+421 xxx xxx xxx"
                data-testid="register-phone"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="password">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Minimálne 6 znakov"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="confirmPassword">
                {t('auth.password_confirm')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder={t('auth.password_confirm_placeholder')}
                data-testid="register-confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition disabled:bg-gray-300 disabled:cursor-not-allowed mt-6"
              data-testid="register-submit"
            >
              {loading ? t('auth.registering') : t('auth.register_action')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {t('auth.have_account')}{' '}
              <Link to="/login" className="text-orange-500 hover:text-orange-600 font-semibold">
                {t('auth.login_action')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;